import { Component, computed, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { BreadcrumbService } from '../../../shared/components/breadcrumbs/breadcrumbs';
import { VoiceTestResult } from '../../../shared/models/voice-testing.model';
import { Role } from '../../../shared/models/user.model';
import { DialogRef, ModalService } from '../../../shared/services/modal.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { ReimburseUtils } from '../../../shared/services/reimburse-utils';
import { UsersService } from '../../../shared/services/users.service';
import { SearchParams, VoiceTestingService } from '../../../shared/services/voice-testing.service';
import { SipFormComponent } from '../sip-form/sip-form.component';
import { SipResultsTableComponent } from '../sip-results-table/sip-results-table.component';
import { SipSearchFormComponent } from '../sip-search-form/sip-search-form.component';

@Component({
  standalone: false,
  selector: 'app-sip-index',
  templateUrl: 'sip-index.component.html',
  styleUrls: ['sip-index.component.scss'],
})
export class SipIndexComponent implements OnInit {
  @ViewChild(SipFormComponent, { static: false }) form: SipFormComponent;
  @ViewChild(SipResultsTableComponent, { static: false }) results: SipResultsTableComponent;
  @ViewChild(SipSearchFormComponent, { static: false }) filter: SipSearchFormComponent;
  @ViewChild('reimburseModalTpl', { read: TemplateRef, static: false }) reimburseModalTpl: TemplateRef<HTMLTemplateElement>;

  pauseUpdateForm = false;
  pauseUpdateResults = false;

  testingDisabledVoice = null;
  testingDisabledSip = null;
  isAdmin = false;

  readonly selectedRows = signal<VoiceTestResult[]>([]);
  readonly hasReimbursableTests = computed(() => this.selectedRows().some(row => ReimburseUtils.canReimburse(row)));

  constructor(
    public breadcrumbs: BreadcrumbService,
    public router: Router,
    public route: ActivatedRoute,
    public notificationService: NotificationService,
    public userService: UsersService,
    private modal: ModalService,
    private voiceTestingService: VoiceTestingService,
    titleService: Title
  ) {
    titleService.setTitle('SIP CLI testing');
  }

  afterSaveTestCase() {
    this.pauseUpdateResults = false;
    this.results.currentPageNumber = 1;
    this.results.update();
  }

  ngOnInit() {
    this.breadcrumbs.root('Dashboard', ['dashboard']).add('Test results', ['sip-testing'], true);
    this.userService.getAuthUser().then(user => {
      this.isAdmin = user.role === Role.ADMIN;
    });
  }

  onSubmitFilter(event: SearchParams) {
    if (this.results.loading) {
      return;
    }
    this.results.searchParams = event;
    this.results.currentPageNumber = 1;
    this.results.update();
  }

  batchClear() {
    this.selectedRows.set([]);
    this.results.resetBatch();
  }

  onBatchReimburse(): void {
    const dialogRef: DialogRef = this.modal.alert().dialogClass('modal-dialog small-modal').component(this.reimburseModalTpl).open();
    dialogRef.result.then(result => {
      if (!result) {
        this.notificationService.info('Reimbursement canceled', 'Voice Testing');
        return;
      }

      const reimbursableTests = this.selectedRows().filter(row => ReimburseUtils.canReimburse(row));
      const testIds = reimbursableTests.map(row => row.id);

      this.results.loading = true;
      this.voiceTestingService.reimburse(testIds).subscribe({
        next: () => {
          this.notificationService.success('Tests reimbursed', 'Voice Testing');
          this.results.update();
          this.batchClear();
        },
        error: e => {
          this.notificationService.error({
            title: 'Reimburse tests',
            message: 'An error occurred while reimbursing the tests',
            serviceName: 'VTC',
            requestMessage: e.statusText,
            requestCode: e.status,
            ts: e.timestamp ? e.timestamp : null,
          });
          this.results.loading = false;
        },
      });
    });
  }
}
